import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Smartphone, Share2 } from "lucide-react";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports MacIntel but has touch
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPWA({ variant = "outline", size = "sm", className = "", label = "Installa App" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIOS, setShowIOS] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const canPrompt = !!deferred;
  const showBtn = canPrompt || ios; // Android/desktop con prompt, iOS con istruzioni manuali

  if (!showBtn) return null;

  const onClick = async () => {
    if (canPrompt) {
      try {
        deferred.prompt();
        await deferred.userChoice;
      } catch {}
      setDeferred(null);
    } else if (ios) {
      setShowIOS(true);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={onClick}
        className={className}
        data-testid="install-pwa-btn"
      >
        <Download className="h-4 w-4 mr-2" /> {label}
      </Button>

      <Dialog open={showIOS} onOpenChange={setShowIOS}>
        <DialogContent data-testid="install-ios-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" /> Installa su iPhone / iPad
            </DialogTitle>
            <DialogDescription>
              Safari non ha un pulsante automatico. Bastano 3 passaggi:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="h-6 w-6 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</span>
              <span className="flex items-center gap-1">
                Tocca l'icona <Share2 className="inline h-4 w-4 mx-1" /> <b>Condividi</b> in basso al centro.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</span>
              <span>Scorri e tocca <b>"Aggiungi a Home"</b>.</span>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">3</span>
              <span>Conferma con <b>"Aggiungi"</b>: l'icona Cantiere360 compare sulla Home.</span>
            </li>
          </ol>
          <div className="pt-2 text-xs text-muted-foreground">
            Da quel momento apri l'app come un'app normale: niente barra del browser, funziona anche offline.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
